"use client";

import katex from "katex";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  ACCENT_SNIPPETS,
  type EquationSnippet,
  equationDocument,
  GREEK_SNIPPETS,
  insertSnippet,
  mathElement,
  mathmlForWord,
  OPERATOR_SNIPPETS,
  STRUCTURE_SNIPPETS,
  withDelimiters,
} from "@/lib/tool-kit/developer/latex-equation";
import { downloadText } from "@/lib/tool-kit/download";

/** English strings. A translated content file overrides these through `ui`. */
export const LATEX_EQUATION_EDITOR_STRINGS = {
  inputLabel: "LaTeX",
  inputHint:
    "Type the math itself, without $ signs. Rendered as you type by KaTeX, in your browser.",
  sample: "x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}",
  modeLabel: "Layout",
  modes: {
    display: "Display — on its own line",
    inline: "Inline — inside a sentence",
  },
  toolbarLabel: "Insert",
  groups: {
    structures: "Structures",
    greek: "Greek letters",
    operators: "Operators and relations",
    accents: "Accents and fonts",
  },
  snippetNames: {
    fraction: "Fraction",
    sqrt: "Square root",
    root: "nth root",
    superscript: "Superscript",
    subscript: "Subscript",
    sum: "Sum",
    product: "Product",
    integral: "Integral",
    definite: "Definite integral",
    limit: "Limit",
    parentheses: "Parentheses that grow",
    binomial: "Binomial coefficient",
    matrix: "Matrix",
    cases: "Cases",
    aligned: "Aligned equations",
    text: "Text inside math",
    hat: "Hat",
    bar: "Bar",
    overline: "Overline",
    vec: "Vector arrow",
    dot: "Dot",
    ddot: "Double dot",
    tilde: "Tilde",
    mathbb: "Blackboard bold",
    mathrm: "Upright letters",
  },
  insertCommand: "Insert \\{command}",
  previewHeading: "Preview",
  error: "KaTeX could not read the LaTeX at character {position}: {message}",
  warning: "This renders here, but standard LaTeX may reject it: {message}",
  emptyPrompt: "Type an equation above to see it rendered.",
  tooLong: "Equations here are limited to {limit} characters.",
  copyLatex: "Copy LaTeX",
  copyInline: "Copy as $…$",
  copyDisplay: "Copy with display brackets",
  copyMathml: "Copy MathML for Word",
  download: "Download .html",
  downloadFilename: "equation.html",
  documentTitle: "Equation",
  copied: "Copied!",
  mathmlSummary: "Show the MathML",
  mathmlLabel: "MathML",
} as const;

type Strings = typeof LATEX_EQUATION_EDITOR_STRINGS;
type Layout = "display" | "inline";
type SnippetName = keyof Strings["snippetNames"];

const LAYOUTS: readonly Layout[] = ["display", "inline"];
const MAX_LENGTH = 5000;

const GROUPS: readonly [keyof Strings["groups"], readonly EquationSnippet[]][] =
  [
    ["structures", STRUCTURE_SNIPPETS],
    ["greek", GREEK_SNIPPETS],
    ["operators", OPERATOR_SNIPPETS],
    ["accents", ACCENT_SNIPPETS],
  ];

type Rendered =
  | { ok: true; html: string; warnings: string[] }
  | { ok: false; message: string; position: number };

/** KaTeX → MathML. `trust: false` keeps \href, \url and \includegraphics inert. */
function renderTex(tex: string, display: boolean): Rendered {
  const warnings: string[] = [];
  try {
    const html = katex.renderToString(tex, {
      displayMode: display,
      output: "mathml",
      throwOnError: true,
      trust: false,
      strict: (_code: string, message: string) => {
        if (!warnings.includes(message)) {
          warnings.push(message);
        }
        return "ignore";
      },
    });
    return { ok: true, html, warnings };
  } catch (error) {
    if (error instanceof katex.ParseError) {
      return { ok: false, message: error.rawMessage, position: error.position };
    }
    return { ok: false, message: String(error), position: 0 };
  }
}

function snippetName(snippet: EquationSnippet, strings: Strings): string {
  const names: Record<string, string> = strings.snippetNames;
  return (
    names[snippet.id as SnippetName] ??
    t(strings.insertCommand, { command: snippet.id })
  );
}

function Toolbar({
  strings,
  onInsert,
}: {
  strings: Strings;
  onInsert: (snippet: EquationSnippet) => void;
}) {
  return (
    <div className={`${CARD} space-y-3`}>
      <p className={LABEL}>{strings.toolbarLabel}</p>
      {GROUPS.map(([group, snippets]) => (
        <div className="space-y-1" key={group}>
          <p className={HINT}>{strings.groups[group]}</p>
          <div className="flex flex-wrap gap-1">
            {snippets.map((snippet) => {
              const name = snippetName(snippet, strings);
              return (
                <button
                  aria-label={name}
                  className={`${BUTTON_SMALL} min-w-9 font-serif text-sm`}
                  key={snippet.id}
                  onClick={() => onInsert(snippet)}
                  title={name}
                  type="button"
                >
                  {snippet.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function LatexEquationEditorWidget() {
  const strings = useToolUi(LATEX_EQUATION_EDITOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [tex, setTex] = useState<string>(strings.sample);
  const [layout, setLayout] = useState<Layout>("display");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pendingCursor = useRef<number | null>(null);
  const display = layout === "display";

  useEffect(() => {
    const cursor = pendingCursor.current;
    const input = inputRef.current;
    if (cursor !== null && input) {
      input.focus();
      input.setSelectionRange(cursor, cursor);
      pendingCursor.current = null;
    }
  });

  const tooLong = tex.length > MAX_LENGTH;
  const rendered = useMemo(
    () => (tex.trim() && !tooLong ? renderTex(tex, display) : null),
    [tex, display, tooLong]
  );

  function insert(snippet: EquationSnippet) {
    const input = inputRef.current;
    const start = input?.selectionStart ?? tex.length;
    const end = input?.selectionEnd ?? tex.length;
    const next = insertSnippet(tex, start, end, snippet);
    pendingCursor.current = next.cursor;
    setTex(next.value);
  }

  function renderPreview() {
    if (tooLong) {
      return (
        <ErrorNotice>
          {t(strings.tooLong, { limit: fmt(MAX_LENGTH) })}
        </ErrorNotice>
      );
    }
    if (rendered === null) {
      return <Notice>{strings.emptyPrompt}</Notice>;
    }
    if (!rendered.ok) {
      return (
        <ErrorNotice>
          {t(strings.error, {
            position: fmt(rendered.position + 1),
            message: rendered.message,
          })}
        </ErrorNotice>
      );
    }
    const mathml = mathmlForWord(rendered.html);
    return (
      <>
        <div
          className="overflow-x-auto rounded-lg border border-border bg-card px-4 py-6 text-center text-2xl text-foreground"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: KaTeX output with trust off — only the MathML elements KaTeX builds, all text escaped by KaTeX.
          dangerouslySetInnerHTML={{ __html: rendered.html }}
        />
        {rendered.warnings.map((warning) => (
          <Notice key={warning}>
            {t(strings.warning, { message: warning })}
          </Notice>
        ))}
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyLatex}
            text={tex.trim()}
          />
          <CopyButton
            copiedLabel={strings.copied}
            label={display ? strings.copyDisplay : strings.copyInline}
            text={withDelimiters(tex, display)}
          />
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyMathml}
            text={mathml}
          />
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              downloadText(
                strings.downloadFilename,
                equationDocument(
                  mathElement(rendered.html),
                  strings.documentTitle,
                  locale
                ),
                "text/html;charset=utf-8"
              )
            }
            type="button"
          >
            {strings.download}
          </button>
        </div>
        <details className="space-y-2">
          <summary className="cursor-pointer text-primary text-sm">
            {strings.mathmlSummary}
          </summary>
          <label className={`${LABEL} mt-2 block`} htmlFor="leq-mathml">
            {strings.mathmlLabel}
          </label>
          <textarea
            className={TEXTAREA}
            id="leq-mathml"
            readOnly
            rows={6}
            spellCheck={false}
            value={mathml}
          />
        </details>
      </>
    );
  }

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <label className={LABEL} htmlFor="leq-input">
          {strings.inputLabel}
        </label>
        <textarea
          className={TEXTAREA}
          id="leq-input"
          onChange={(event) => setTex(event.target.value)}
          ref={inputRef}
          rows={4}
          spellCheck={false}
          value={tex}
        />
        <p className={HINT}>{strings.inputHint}</p>
      </div>
      <Toolbar onInsert={insert} strings={strings} />
      <PillGroup
        label={strings.modeLabel}
        name="leq-layout"
        onChange={setLayout}
        options={LAYOUTS.map((value) => ({
          value,
          label: strings.modes[value],
        }))}
        value={layout}
      />
      <div aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.previewHeading}</h2>
        {renderPreview()}
      </div>
    </section>
  );
}
